import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';

export type DeviceSpareOption = 'spare' | 'not_spare';

const SPARE_OPTIONS: DeviceSpareOption[] = ['spare', 'not_spare'];

export interface MultiSelectDeviceSpareProps {
  size?: 'sm' | 'md';
  selectedSpare: DeviceSpareOption[];
  onChange: (spare: DeviceSpareOption[]) => void;
}

const MultiSelectDeviceSpare = ({ size = 'md', selectedSpare, onChange }: MultiSelectDeviceSpareProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState<DeviceSpareOption[]>(selectedSpare || []);

  const hasNotChanged = selectedSpare?.length === draft?.length && selectedSpare.every((value) => draft?.includes(value));

  const onClick = useCallback(() => {
    setDraft(selectedSpare || []);
  }, [selectedSpare]);

  const handleClear = useCallback(() => {
    setDraft([]);
  }, []);

  const handleClose = useCallback(() => {
    setForceClosed(true);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const toggleItem = useCallback(
    (spare: DeviceSpareOption) => {
      const existItem = draft.includes(spare);
      const newDraft = existItem ? draft.filter((value) => value !== spare) : [...draft, spare];
      setDraft(newDraft);
    },
    [draft]
  );

  const getSpareLabel = (spare: DeviceSpareOption) => {
    switch (spare) {
      case 'spare':
        return t('device_spare.spare');
      case 'not_spare':
        return t('device_spare.not_spare');
    }
  };

  return (
    <Dropdown
      onOpenChange={onClick}
      forceClose={forceClosed}
      portal={false}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={handleClear}>
              {t('device_spare.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-4'>
            <Dropdown.List
              heightItem={30}
              data={SPARE_OPTIONS}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item: DeviceSpareOption) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={`spare-${item}`} key={item} checked={draft.includes(item)} onCheckedChange={() => toggleItem(item)} />
                  <Label htmlFor={`spare-${item}`}>{getSpareLabel(item)}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button variant='ghost' full size='sm' onClick={handleClose}>
                {t('device_spare.cancel')}
              </Button>
              <Button size='sm' full onClick={handleApply} disabled={hasNotChanged}>
                {t('device_spare.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='package' />}>
        {t('device_spare.title')} {(selectedSpare?.length || 0) > 0 ? `(${selectedSpare?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectDeviceSpare;
